import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useMsal } from '@azure/msal-react'
import { ArrowUpRight, FileText, PenLine, RefreshCw, Scissors, Smile, Copy as CopyIcon, PlusSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select } from '@/components/ui/select'
import { apiClient, TokenExpiredError } from '@/lib/api'
import { authService } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { fetchCurrentEmailContext, insertDraftIntoCompose, isComposeMode } from './office/outlook'
import {
  clearOutlookAddinToken,
  getOutlookAddinToken,
  setOutlookAddinToken,
  signInWithBackendPopup,
} from './utils/auth'
import { getOrCreateSessionId } from './utils/session'
import type {
  OutlookDraftReplyResponse,
  OutlookEmailContext,
  OutlookSummarizeResponse,
  OutlookTone,
} from './types'

const TONE_OPTIONS: Array<{ label: string; value: OutlookTone }> = [
  { label: 'Professional', value: 'professional' },
  { label: 'Friendly', value: 'friendly' },
  { label: 'Short', value: 'short' },
  { label: 'Firm', value: 'firm' },
]

function truncatePreview(text: string, max = 320): string {
  const t = (text || '').trim()
  if (t.length <= max) return t
  return t.slice(0, max) + '…'
}

export function OutlookAddinPage() {
  const { instance, accounts } = useMsal()

  const sessionId = useMemo(() => getOrCreateSessionId(), [])
  const [email, setEmail] = useState<OutlookEmailContext | null>(null)
  const [tone, setTone] = useState<OutlookTone>('professional')
  const [userIntent, setUserIntent] = useState('')
  const [summary, setSummary] = useState('')
  const [drafts, setDrafts] = useState<string[]>([])
  const [expanded, setExpanded] = useState(false)
  const [bubbleOpen, setBubbleOpen] = useState(true)
  const [lastOutputType, setLastOutputType] = useState<'summary' | 'draft' | null>(null)
  const [summaryStage, setSummaryStage] = useState<'idle' | 'reading' | 'cleaning' | 'generating' | 'done' | 'error'>('idle')
  const [summaryProgress, setSummaryProgress] = useState(0) // 0..100
  const [draftStage, setDraftStage] = useState<'idle' | 'reading' | 'cleaning' | 'generating' | 'done' | 'error'>('idle')
  const [draftProgress, setDraftProgress] = useState(0) // 0..100

  const summaryTimersRef = useRef<number[]>([])
  const summaryProgressTimerRef = useRef<number | null>(null)
  const draftTimersRef = useRef<number[]>([])
  const draftProgressTimerRef = useRef<number | null>(null)
  const verifiedTokenRef = useRef<string | null>(null)
  const interactiveTokenPromiseRef = useRef<Promise<string> | null>(null)

  function clearSummaryTimers() {
    for (const t of summaryTimersRef.current) window.clearTimeout(t)
    summaryTimersRef.current = []
    if (summaryProgressTimerRef.current) {
      window.clearInterval(summaryProgressTimerRef.current)
      summaryProgressTimerRef.current = null
    }
  }

  function clearDraftTimers() {
    for (const t of draftTimersRef.current) window.clearTimeout(t)
    draftTimersRef.current = []
    if (draftProgressTimerRef.current) {
      window.clearInterval(draftProgressTimerRef.current)
      draftProgressTimerRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      clearSummaryTimers()
      clearDraftTimers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [loadingEmail, setLoadingEmail] = useState(false)
  const [summarizeLoading, setSummarizeLoading] = useState(false)
  const [draftLoading, setDraftLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [isCompose, setIsCompose] = useState(false)

  useEffect(() => {
    // Best-effort: set compose state early (mock mode = false)
    try {
      setIsCompose(isComposeMode())
    } catch {
      setIsCompose(false)
    }
  }, [email?.item_id])

  function clearLocalTokenState() {
    apiClient.clearAuthToken()
    verifiedTokenRef.current = null
    clearOutlookAddinToken()
  }

  async function validateAndUseToken(token?: string | null): Promise<string | null> {
    const candidate = token?.trim()
    if (!candidate) return null

    apiClient.setAuthToken(candidate)
    if (verifiedTokenRef.current === candidate) {
      return candidate
    }

    try {
      await apiClient.getCurrentUser()
      verifiedTokenRef.current = candidate
      return candidate
    } catch {
      if (apiClient.getAuthToken() === candidate) {
        apiClient.clearAuthToken()
      }
      if (verifiedTokenRef.current === candidate) {
        verifiedTokenRef.current = null
      }
      return null
    }
  }

  async function ensureToken(options?: { interactive?: boolean }): Promise<string> {
    const interactive = options?.interactive ?? true
    const allowBackendPopupFallback = window.self !== window.top

    const tokenFromApiClient = apiClient.getAuthToken()
    const validApiClientToken = await validateAndUseToken(tokenFromApiClient)
    if (validApiClientToken) {
      return validApiClientToken
    }

    const storedOutlookToken = getOutlookAddinToken()
    const validStoredOutlookToken = await validateAndUseToken(storedOutlookToken)
    if (validStoredOutlookToken) {
      return validStoredOutlookToken
    }
    if (storedOutlookToken) {
      clearOutlookAddinToken()
    }

    const existing = await authService.getToken()
    const validExisting = await validateAndUseToken(existing)
    if (validExisting) {
      setOutlookAddinToken(validExisting)
      return validExisting
    }

    const scopes = ['User.Read']
    const account = accounts?.[0]
    if (account) {
      try {
        const silent = await instance.acquireTokenSilent({ account, scopes })
        const validSilent = await validateAndUseToken(silent.accessToken)
        if (validSilent) {
          setOutlookAddinToken(validSilent, silent.expiresOn)
          return validSilent
        }
      } catch {
        // fallthrough to popup if interactive
      }
    }

    if (!interactive) {
      throw new Error('Authentication required')
    }

    if (interactiveTokenPromiseRef.current) {
      return interactiveTokenPromiseRef.current
    }

    interactiveTokenPromiseRef.current = (async () => {
      let msalFailure: unknown

      try {
        const popup = await instance.acquireTokenPopup({ scopes })
        const validPopup = await validateAndUseToken(popup.accessToken)
        if (validPopup) {
          setOutlookAddinToken(validPopup, popup.expiresOn)
          return validPopup
        }
      } catch (e) {
        msalFailure = e
      }

      if (allowBackendPopupFallback) {
        const backendPopupToken = await signInWithBackendPopup()
        const validBackendPopupToken = await validateAndUseToken(backendPopupToken)
        if (validBackendPopupToken) {
          setOutlookAddinToken(validBackendPopupToken)
          return validBackendPopupToken
        }
      }

      if (msalFailure instanceof Error && msalFailure.message) {
        throw new Error(msalFailure.message)
      }
      throw new Error('Failed to validate sign-in token')
    })()

    try {
      return await interactiveTokenPromiseRef.current
    } finally {
      interactiveTokenPromiseRef.current = null
    }
  }

  async function executeWithTokenRefresh<T>(
    operation: () => Promise<T>,
    options?: { interactive?: boolean }
  ): Promise<T> {
    await ensureToken({ interactive: options?.interactive ?? true })

    try {
      return await operation()
    } catch (e) {
      if (e instanceof TokenExpiredError) {
        clearLocalTokenState()
        await ensureToken({ interactive: false })
        return await operation()
      }
      throw e
    }
  }

  async function postClientEvent(action: 'pane_opened' | 'fetch_email' | 'copy_clicked' | 'insert_clicked', meta?: Record<string, any>) {
    try {
      await executeWithTokenRefresh(
        async () => {
          await apiClient.outlookEvent({
            client: 'outlook_addin',
            action,
            session_id: sessionId,
            context_hash: undefined,
            tone,
            meta,
          })
        },
        { interactive: false }
      )
    } catch {
      // telemetry should not block UX
    }
  }

  useEffect(() => {
    postClientEvent('pane_opened')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function onFetchEmail() {
    setError(null)
    setLoadingEmail(true)
    try {
      await ensureToken({ interactive: true })
      const ctx = await fetchCurrentEmailContext()
      setEmail(ctx)
      setIsCompose(ctx.is_compose)
      setBubbleOpen(true)
      if (!ctx.thread_text?.trim()) {
        setError('Email body is empty or unavailable. Open the message content and try Fetch again.')
      }
      await postClientEvent('fetch_email')
    } catch (e: any) {
      if (e instanceof TokenExpiredError) {
        clearLocalTokenState()
        setError('Session expired. Please sign in again.')
      } else {
        setError(e?.message || 'Failed to fetch email')
      }
    } finally {
      setLoadingEmail(false)
    }
  }

  async function onSummarize() {
    setError(null)
    setSummarizeLoading(true)
    clearSummaryTimers()
    setSummaryStage('reading')
    setSummaryProgress(8)

    // Deterministic, non-looping progress:
    // Advance by stage-timeouts, then slowly approach 90% until the request completes.
    summaryTimersRef.current.push(
      window.setTimeout(() => {
        setSummaryStage((s: typeof summaryStage) => (s === 'reading' ? 'cleaning' : s))
        setSummaryProgress((p: number) => Math.max(p, 35))
      }, 300)
    )
    summaryTimersRef.current.push(
      window.setTimeout(() => {
        setSummaryStage((s: typeof summaryStage) => (s === 'reading' || s === 'cleaning' ? 'generating' : s))
        setSummaryProgress((p: number) => Math.max(p, 70))
      }, 800)
    )
    summaryProgressTimerRef.current = window.setInterval(() => {
      setSummaryProgress((p: number) => {
        // approach 90%, never loop
        if (p >= 90) return p
        const next = p + Math.max(0.6, (90 - p) * 0.06)
        return Math.min(90, Math.round(next))
      })
    }, 250)
    try {
      const ctx = email ?? (await fetchCurrentEmailContext())
      setEmail(ctx)
      setIsCompose(ctx.is_compose)

      const res: OutlookSummarizeResponse = await executeWithTokenRefresh(
        () =>
          apiClient.outlookSummarize({
            client: 'outlook_addin',
            action: 'summarize',
            session_id: sessionId,
            item_id: ctx.item_id,
            subject: ctx.subject,
            from_email: ctx.from_email,
            to_emails: ctx.to_emails,
            cc_emails: ctx.cc_emails,
            sent_at: ctx.sent_at,
            thread_text: ctx.thread_text,
            tone,
          }),
        { interactive: true }
      )
      const summaryText = (res.summary || '').trim()
      if (!summaryText) {
        throw new Error('Summary returned empty content. Please retry.')
      }
      setSummary(summaryText)
      setLastOutputType('summary')
      setBubbleOpen(true)
      setSummaryStage('done')
      clearSummaryTimers()
      setSummaryProgress(100)
      summaryTimersRef.current.push(
        window.setTimeout(() => {
          setSummaryStage('idle')
          setSummaryProgress(0)
        }, 900)
      )
    } catch (e: any) {
      if (e instanceof TokenExpiredError) {
        clearLocalTokenState()
        setError('Session expired. Please sign in again.')
      } else {
        setError(e?.message || 'Summarize failed')
      }
      setSummaryStage('error')
      clearSummaryTimers()
      setSummaryProgress((p: number) => (p < 85 ? 85 : p))
    } finally {
      setSummarizeLoading(false)
    }
  }

  async function onGenerateDrafts(forcedTone?: OutlookTone) {
    setError(null)
    setDraftLoading(true)
    clearDraftTimers()
    setDraftStage('reading')
    setDraftProgress(8)

    draftTimersRef.current.push(
      window.setTimeout(() => {
        setDraftStage((s: typeof draftStage) => (s === 'reading' ? 'cleaning' : s))
        setDraftProgress((p: number) => Math.max(p, 30))
      }, 250)
    )
    draftTimersRef.current.push(
      window.setTimeout(() => {
        setDraftStage((s: typeof draftStage) => (s === 'reading' || s === 'cleaning' ? 'generating' : s))
        setDraftProgress((p: number) => Math.max(p, 70))
      }, 800)
    )
    draftProgressTimerRef.current = window.setInterval(() => {
      setDraftProgress((p: number) => {
        // approach 92%, never loop
        if (p >= 92) return p
        const next = p + Math.max(0.6, (92 - p) * 0.06)
        return Math.min(92, Math.round(next))
      })
    }, 250)
    try {
      const ctx = email ?? (await fetchCurrentEmailContext())
      setEmail(ctx)
      setIsCompose(ctx.is_compose)
      const actualTone = forcedTone ?? tone
      if (forcedTone) setTone(forcedTone)

      const res: OutlookDraftReplyResponse = await executeWithTokenRefresh(
        () =>
          apiClient.outlookDraftReply({
            client: 'outlook_addin',
            action: 'draft_reply',
            session_id: sessionId,
            item_id: ctx.item_id,
            subject: ctx.subject,
            from_email: ctx.from_email,
            to_emails: ctx.to_emails,
            cc_emails: ctx.cc_emails,
            sent_at: ctx.sent_at,
            thread_text: ctx.thread_text,
            summary: summary || undefined,
            user_intent: userIntent || undefined,
            tone: actualTone,
            variants: 1,
          }),
        { interactive: true }
      )
      const nonEmptyDrafts = (res.drafts || [])
        .map((d: string) => (d || '').trim())
        .filter((d: string) => d.length > 0)
      if (nonEmptyDrafts.length === 0) {
        throw new Error('Draft returned empty content. Please retry.')
      }
      setDrafts(nonEmptyDrafts)
      setLastOutputType('draft')
      setBubbleOpen(true)
      setDraftStage('done')
      clearDraftTimers()
      setDraftProgress(100)
      draftTimersRef.current.push(
        window.setTimeout(() => {
          setDraftStage('idle')
          setDraftProgress(0)
        }, 900)
      )
    } catch (e: any) {
      if (e instanceof TokenExpiredError) {
        clearLocalTokenState()
        setError('Session expired. Please sign in again.')
      } else {
        setError(e?.message || 'Draft generation failed')
      }
      setDraftStage('error')
      clearDraftTimers()
      setDraftProgress((p: number) => (p < 85 ? 85 : p))
    } finally {
      setDraftLoading(false)
    }
  }

  async function copyDraft(text: string, index: number) {
    try {
      await navigator.clipboard.writeText(text)
      await postClientEvent('copy_clicked', { draft_index: index })
    } catch (e: any) {
      setError(e?.message || 'Copy failed')
    }
  }

  async function insertDraft(text: string, index: number) {
    try {
      await insertDraftIntoCompose(text)
      await postClientEvent('insert_clicked', { draft_index: index })
    } catch (e: any) {
      setError(e?.message || 'Insert failed')
    }
  }

  const emailPreview = email ? truncatePreview(email.thread_text, 420) : ''
  const bubbleText =
    lastOutputType === 'summary'
      ? summary
      : lastOutputType === 'draft'
        ? drafts[0] || ''
        : ''
  const fetchStatusText = loadingEmail
    ? 'Fetching email context...'
    : email
      ? `Email loaded${email.subject ? `: ${truncatePreview(email.subject, 64)}` : ''}`
      : 'No email loaded'
  // Do not truncate bubble content; use scroll instead.
  const bubblePreview = bubbleText
  const showSummaryStatus = summarizeLoading || summaryStage === 'reading' || summaryStage === 'cleaning' || summaryStage === 'generating' || summaryStage === 'error'
  const showDraftStatus =
    draftLoading ||
    draftStage === 'reading' ||
    draftStage === 'cleaning' ||
    draftStage === 'generating' ||
    draftStage === 'error'
  const retryHintText = error || 'Check your sign-in status/network and try again.'

  return (
    <div className="h-screen bg-background">
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col">
        {/* Expanded details (optional) */}
        {expanded && (
          <div className="flex-1 overflow-auto p-4">
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-base font-semibold leading-tight">Giatec Email Assistant</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Details view (expand/collapse).
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setExpanded(false)}>
                  Collapse
                </Button>
              </div>

              {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              {email ? (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Email context</CardTitle>
                    <CardDescription className="break-words">
                      <span className="font-medium">Subject:</span> {email.subject || '(none)'}{' '}
                      <span className="ml-2 font-medium">From:</span> {email.from_email || '(unknown)'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 text-xs leading-relaxed">
                      {emailPreview || '(empty body)'}
                    </pre>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">No email loaded yet</CardTitle>
                    <CardDescription>Use Fetch Email in the bar below.</CardDescription>
                  </CardHeader>
                </Card>
              )}

              {summary && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 text-sm leading-relaxed">{summary}</pre>
                  </CardContent>
                </Card>
              )}

              {drafts.length > 0 && (
                <div className="space-y-3">
                  {drafts.map((d: string, idx: number) => (
                    <Card key={idx}>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Draft {String.fromCharCode(65 + idx)}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <pre className={cn('whitespace-pre-wrap rounded-md bg-muted p-3 text-sm leading-relaxed')}>
                          {d}
                        </pre>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => copyDraft(d, idx)}>
                            Copy
                          </Button>
                          <Button size="sm" onClick={() => insertDraft(d, idx)} disabled={!isCompose}>
                            Insert
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Minimal “embedded-like” area (always present) */}
        {!expanded && <div className="flex-1" />}

        {/* Bubble preview / status */}
        {bubbleOpen && bubblePreview && (
          <div className="pointer-events-none px-3 pb-2">
            <div className="pointer-events-auto ml-auto w-full max-w-[560px] rounded-lg border bg-card p-3 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className={cn('inline-flex h-7 w-7 items-center justify-center rounded-md',
                    lastOutputType === 'summary' ? 'bg-primary/10 text-primary' : lastOutputType === 'draft' ? 'bg-primary/10 text-primary' : 'bg-muted text-foreground/70'
                  )}>
                    {lastOutputType === 'summary' ? <FileText className="h-4 w-4" /> : lastOutputType === 'draft' ? <PenLine className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                  </span>
                  <span>{lastOutputType === 'summary' ? 'Summary' : lastOutputType === 'draft' ? 'Draft ready' : 'Result'}</span>
                </div>
                <button
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setBubbleOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div
                className={cn(
                  'mt-2 whitespace-pre-wrap text-sm text-foreground/90',
                  lastOutputType === 'summary' || lastOutputType === 'draft'
                    ? 'max-h-[45vh] overflow-auto pr-2'
                    : ''
                )}
              >
                {bubblePreview}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    await navigator.clipboard.writeText(bubbleText)
                  }}
                >
                  <CopyIcon className="mr-1.5 h-4 w-4" />
                  Copy
                </Button>
                {lastOutputType === 'draft' ? (
                  <Button
                    size="sm"
                    onClick={() => insertDraft(drafts[0] || '', 0)}
                    disabled={!isCompose || !drafts[0]}
                  >
                    <PlusSquare className="mr-1.5 h-4 w-4" />
                    Insert
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {isCompose ? 'Compose mode' : 'Read mode'}
                  </span>
                )}
                {!isCompose && lastOutputType === 'draft' && (
                  <span className="text-xs text-muted-foreground">Click Reply to enter compose, then insert.</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Summary processing status bubble (scheme 3) */}
        {showSummaryStatus && (
          <div className="pointer-events-none px-3 pb-2">
            <div className="pointer-events-auto ml-auto w-full max-w-[560px] rounded-lg border bg-card p-3 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className={cn('inline-flex h-7 w-7 items-center justify-center rounded-md', summaryStage === 'error' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary')}>
                    <FileText className="h-4 w-4" />
                  </span>
                  <span>{summaryStage === 'error' ? 'Summary failed' : 'Summarizing'}</span>
                </div>
                <button
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    clearSummaryTimers()
                    setSummaryStage('idle')
                    setSummaryProgress(0)
                    setSummarizeLoading(false)
                  }}
                  title="Stop"
                >
                  ✕
                </button>
              </div>

              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn('h-full rounded-full', summaryStage === 'error' ? 'bg-destructive' : 'bg-primary')}
                  style={{ width: `${Math.min(100, Math.max(0, summaryProgress))}%` }}
                />
              </div>

              <div className="mt-3 space-y-1 text-sm">
                <div className={cn('flex items-center justify-between', summaryStage === 'reading' ? 'text-foreground' : 'text-muted-foreground')}>
                  <span>Reading email…</span>
                  <span>{summaryStage !== 'idle' && summaryStage !== 'error' && (summaryStage === 'reading' ? '…' : '✓')}</span>
                </div>
                <div className={cn('flex items-center justify-between', summaryStage === 'cleaning' ? 'text-foreground' : 'text-muted-foreground')}>
                  <span>Cleaning content…</span>
                  <span>{summaryStage === 'reading' ? '' : summaryStage === 'cleaning' ? '…' : summaryStage === 'error' ? '!' : '✓'}</span>
                </div>
                <div className={cn('flex items-center justify-between', summaryStage === 'generating' ? 'text-foreground' : 'text-muted-foreground')}>
                  <span>Generating summary…</span>
                  <span>{summaryStage === 'generating' ? '…' : summaryStage === 'done' ? '✓' : summaryStage === 'error' ? '!' : ''}</span>
                </div>
              </div>

              {summaryStage === 'error' && (
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" onClick={onSummarize}>
                    Retry
                  </Button>
                  <span className="text-xs text-muted-foreground">{retryHintText}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Draft processing status bubble (scheme 3) */}
        {showDraftStatus && (
          <div className="pointer-events-none px-3 pb-2">
            <div className="pointer-events-auto ml-auto w-full max-w-[560px] rounded-lg border bg-card p-3 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className={cn('inline-flex h-7 w-7 items-center justify-center rounded-md', draftStage === 'error' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary')}>
                    <PenLine className="h-4 w-4" />
                  </span>
                  <span>{draftStage === 'error' ? 'Draft failed' : 'Drafting'}</span>
                </div>
                <button
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    clearDraftTimers()
                    setDraftStage('idle')
                    setDraftProgress(0)
                    setDraftLoading(false)
                  }}
                  title="Stop"
                >
                  ✕
                </button>
              </div>

              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn('h-full rounded-full', draftStage === 'error' ? 'bg-destructive' : 'bg-primary')}
                  style={{ width: `${Math.min(100, Math.max(0, draftProgress))}%` }}
                />
              </div>

              <div className="mt-3 space-y-1 text-sm">
                <div className={cn('flex items-center justify-between', draftStage === 'reading' ? 'text-foreground' : 'text-muted-foreground')}>
                  <span>Reading email…</span>
                  <span>{draftStage !== 'idle' && draftStage !== 'error' && (draftStage === 'reading' ? '…' : '✓')}</span>
                </div>
                <div className={cn('flex items-center justify-between', draftStage === 'cleaning' ? 'text-foreground' : 'text-muted-foreground')}>
                  <span>Cleaning content…</span>
                  <span>{draftStage === 'reading' ? '' : draftStage === 'cleaning' ? '…' : draftStage === 'error' ? '!' : '✓'}</span>
                </div>
                <div className={cn('flex items-center justify-between', draftStage === 'generating' ? 'text-foreground' : 'text-muted-foreground')}>
                  <span>Generating drafts…</span>
                  <span>{draftStage === 'generating' ? '…' : draftStage === 'done' ? '✓' : draftStage === 'error' ? '!' : ''}</span>
                </div>
              </div>

              {draftStage === 'error' && (
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" onClick={() => onGenerateDrafts()}>
                    Retry
                  </Button>
                  <span className="text-xs text-muted-foreground">{retryHintText}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Scheme B docked bar */}
        <div className="border-t bg-background/95 p-3 backdrop-blur">
          {/* Option 2 layout: full-width input row + actions row */}
          <div className="flex flex-col gap-2">
            <input
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              value={userIntent}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setUserIntent(e.target.value)}
              placeholder="Describe your intent…"
            />

            {/* Actions row: never wrap; scroll horizontally when narrow */}
            <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap [-webkit-overflow-scrolling:touch]">
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 rounded-full px-3"
                onClick={onFetchEmail}
                disabled={loadingEmail}
              >
                <RefreshCw className={cn('mr-1.5 h-4 w-4', loadingEmail ? 'animate-spin' : '')} />
                {loadingEmail ? 'Fetching' : 'Fetch'}
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="shrink-0 rounded-full px-3"
                onClick={onSummarize}
                disabled={summarizeLoading}
              >
                <FileText className="mr-1.5 h-4 w-4" />
                {summarizeLoading ? 'Summarizing' : 'Summary'}
              </Button>

              <Button
                size="sm"
                variant="default"
                className="shrink-0 rounded-full px-3"
                onClick={() => onGenerateDrafts()}
                disabled={draftLoading}
              >
                <PenLine className="mr-1.5 h-4 w-4" />
                {draftLoading ? 'Drafting' : 'Draft'}
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="shrink-0 rounded-full px-3"
                onClick={() => onGenerateDrafts('short')}
                disabled={draftLoading}
              >
                <Scissors className="mr-1.5 h-4 w-4" />
                Shorten
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="shrink-0 rounded-full px-3"
                onClick={() => onGenerateDrafts('friendly')}
                disabled={draftLoading}
              >
                <Smile className="mr-1.5 h-4 w-4" />
                Friendlier
              </Button>

              <div className="ml-auto flex shrink-0 items-center gap-2">
                <span className="hidden text-xs text-muted-foreground sm:inline">Tone</span>
                <Select
                  className="h-9 w-[120px] sm:w-[140px]"
                  value={tone}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setTone(e.target.value as OutlookTone)}
                >
                  {TONE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>

                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 rounded-full px-3"
                  onClick={() => setExpanded(true)}
                  title="Expand"
                >
                  <ArrowUpRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>{isCompose ? 'Compose: Insert enabled' : 'Read: Reply → Insert'}</span>
            {error ? <span className="text-destructive">{error}</span> : <span>{fetchStatusText}</span>}
          </div>
        </div>
      </div>
    </div>
  )
}
